import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { TrendingUp, TrendingDown, ShowChart, Refresh } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`top-movers-tabpanel-${index}`}
      aria-labelledby={`top-movers-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TopMovers: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gainers, setGainers] = useState<any[]>([]);
  const [losers, setLosers] = useState<any[]>([]);
  const [mostActive, setMostActive] = useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [gainersData, losersData, activeData] = await Promise.all([
        apiService.getTopGainers(10),
        apiService.getTopLosers(10),
        apiService.getMostActive(10),
      ]);

      setGainers(gainersData.results || []);
      setLosers(losersData.results || []);
      setMostActive(activeData.results || []);
      setLastUpdate(new Date());
    } catch (err: any) {
      console.error('Error fetching top movers:', err);
      setError(err.response?.data?.detail || 'Failed to fetch market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 15 minutes
    const interval = setInterval(() => {
      fetchData();
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`/scoring?symbol=${symbol}`);
  };

  const renderTable = (data: any[], isGainers: boolean = false, isLosers: boolean = false) => {
    if (loading && data.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (data.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No data available. Market may be closed or data is still loading.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell align="right">Last Price</TableCell>
              <TableCell align="right">Change</TableCell>
              <TableCell align="right">% Change</TableCell>
              <TableCell align="right">Volume</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((stock, index) => {
              const changeValue = stock.changePercent || stock.change || 0;
              const isPositive = changeValue >= 0;

              return (
                <TableRow
                  key={`${stock.symbol}-${index}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSymbolClick(stock.symbol || stock.contractSymbol)}
                >
                  <TableCell>
                    <Chip
                      label={index + 1}
                      size="small"
                      color={index === 0 ? 'primary' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isGainers && <TrendingUp fontSize="small" color="success" />}
                      {isLosers && <TrendingDown fontSize="small" color="error" />}
                      <Typography variant="body2" fontWeight={600}>
                        {stock.symbol || stock.contractSymbol}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      ${(stock.lastPrice || stock.close || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={isPositive ? 'success.main' : 'error.main'}
                    >
                      {isPositive ? '+' : ''}{(stock.change || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${isPositive ? '+' : ''}${(changeValue).toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: isPositive
                          ? theme.palette.success.main + '20'
                          : theme.palette.error.main + '20',
                        color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {(stock.volume || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChart />
          <Typography variant="h4" fontWeight={700}>
            Top Movers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Real-time market movers - Top gainers, losers, and most active stocks
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp fontSize="small" />
                  Top Gainers
                  {gainers.length > 0 && (
                    <Chip label={gainers.length} size="small" color="success" />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDown fontSize="small" />
                  Top Losers
                  {losers.length > 0 && (
                    <Chip label={losers.length} size="small" color="error" />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart fontSize="small" />
                  Most Active
                  {mostActive.length > 0 && (
                    <Chip label={mostActive.length} size="small" />
                  )}
                </Box>
              }
            />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderTable(gainers, true, false)}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderTable(losers, false, true)}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderTable(mostActive)}
          </TabPanel>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Tip:</strong> Click on any symbol to view detailed analysis and scoring.
              Data refreshes automatically every 15 minutes.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TopMovers;
